import { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, ArrowUpCircle, AlertTriangle, Lock, Check, ScrollText } from 'lucide-react';
import { RulesSnapshot } from './RulesSnapshot';
import { authApi, engineApi, strategiesApi, type EngineInputs, type Tier, type TierChoice } from '../../lib/api';
import { useFirmCatalog } from '../../hooks/useFirmCatalog';
import { useSkinCopy } from '../../hooks/useSkinCopy';
import { ICE, TEAL, ACCENT, FIELD, PANEL, WIN, LOSS, PX_FONT, TITLE_FONT, NUM_FONT, ON_ACCENT, pixelFrame } from '../guild/theme';

/*
 * Prescription Engine — prop-firm version.
 * Size comes from the trader's worst losing streak (paper + live, last 50 trades; their strategy's
 * planned streak until they have 20) and the firm's drawdown:
 *   Max    survives exactly the worst streak       → DD ÷ S
 *   Push   survives the worst streak plus two      → DD ÷ (S + 2)
 *   Steady survives double it (at least S + 3)     → DD ÷ max(2S, S + 3)
 * A recommendation and move-up / drop-down alerts read the trader's paper and live record.
 */

export const TIERS: Tier[] = ['steady', 'push', 'max'];
export const RANK: Record<Tier, number> = { steady: 0, push: 1, max: 2 };
export const TONE: Record<Tier, string> = { steady: '#4C8DFF', push: '#34F5C5', max: '#F0A04B' };

export const lossesFor = (tier: Tier, s: number) => (tier === 'max' ? Math.max(1, s) : tier === 'push' ? s + 2 : Math.max(2 * s, s + 3));
const usd = (n: number) => `$${Math.round(n).toLocaleString()}`;
const r = (n: number) => `${n >= 0 ? '+' : ''}${n.toFixed(2)}R`;

/** Which tier the record supports, and why */
function recommend(i: EngineInputs): { tier: Tier; why: string[] } {
  const total = i.paper.trades + i.live.trades;
  const exp = i.live.trades >= 20 ? i.live.expectancyR : i.paper.expectancyR;
  const liveHolds = i.live.trades >= 50 && i.live.expectancyR > 0 && i.live.expectancyR >= 0.8 * i.paper.expectancyR;
  if (liveHolds && i.payouts >= 1) {
    return { tier: 'max', why: [`${i.live.trades} live trades holding ${r(i.live.expectancyR)} against paper's ${r(i.paper.expectancyR)}`, `${i.payouts} payout${i.payouts === 1 ? '' : 's'} taken`] };
  }
  if (total >= 50 && exp >= 0.2) {
    return { tier: 'push', why: [`${total} paper + live trades`, `expectancy ${r(exp)}`, liveHolds ? 'take a payout to qualify for Max' : 'Max needs 50 live trades holding up against paper, plus a payout'] };
  }
  return { tier: 'steady', why: [total < 50 ? `${total} of 50 trades so far: start conservative` : `expectancy ${r(exp)} is thin for more size`] };
}

/** Signs the current tier is too hot */
function warnings(i: EngineInputs, choice: TierChoice | null): string[] {
  if (!choice) return [];
  const out: string[] = [];
  if (i.currentLosingStreak >= i.worstStreak) out.push(`${i.currentLosingStreak} losses in a row: you've hit your worst streak`);
  if (i.live.trades >= 10 && i.liveRecent20Expectancy < 0) out.push(`last ${Math.min(20, i.live.trades)} live trades: ${r(i.liveRecent20Expectancy)} expectancy`);
  const dd = Number(choice.drawdown), risk = Number(choice.risk_per_trade);
  if (i.currentDrawdownR * risk >= 0.5 * dd) out.push(`down ${usd(i.currentDrawdownR * risk)} from peak: half your ${usd(dd)} drawdown`);
  return out;
}

const label = { fontFamily: PX_FONT, fontSize: 10, letterSpacing: '0.12em', color: TEAL } as const;
const chip = (on: boolean) => ({
  fontFamily: PX_FONT, fontSize: 11, fontWeight: 700, padding: '6px 11px', border: 'none', borderRadius: 14, cursor: 'pointer',
  background: on ? ACCENT : 'transparent', color: on ? ON_ACCENT : ICE, boxShadow: on ? 'none' : 'inset 0 0 0 1px var(--pal-rim)',
} as const);

/** Everything the engine reads and does, shared by the classic panel and the Grimoire's book */
export function usePrescription(username: string, onChosen?: () => void) {
  const copy = useSkinCopy();
  const { firms: catalog } = useFirmCatalog(username);
  const firms = catalog.map(c => c.data);
  const [admin, setAdmin] = useState(false);
  useEffect(() => { authApi.me(username).then(me => setAdmin(me.roles.includes('admin'))).catch(() => undefined); }, [username]);
  const [strategies, setStrategies] = useState<string[]>([]);
  const [strategy, setStrategy] = useState('VCL');
  const [data, setData] = useState<{ ready: boolean; inputs: EngineInputs; choice: TierChoice | null } | null>(null);
  const [firmId, setFirmId] = useState('apex');
  const [planId, setPlanId] = useState('');
  const [size, setSize] = useState(50000);
  const [funded, setFunded] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [saving, setSaving] = useState<Tier | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { strategiesApi.list(username).then(l => setStrategies(l.map(s => s.code))).catch(() => undefined); }, [username]);
  useEffect(() => {
    setData(null);
    engineApi.get(username, strategy).then(d => {
      setData(d);
      // Reopen on the firm and size the trader locked in last time
      if (d.choice) { setFirmId(d.choice.firm_id); setPlanId(d.choice.plan_id); setSize(d.choice.account_size); }
    }).catch(e => setError(e instanceof Error ? e.message : 'Could not load'));
  }, [username, strategy]);

  const firm = firms.find(f => f.id === firmId) ?? firms[0];
  const entry = catalog.find(c => c.id === firm.id);
  // Rules older than ~2 months are flagged so nobody sizes off stale numbers
  const staleDays = entry?.verified_on ? Math.floor((Date.now() - new Date(entry.verified_on).getTime()) / 86400000) : null;
  const plan = firm.plans.find(p => p.id === planId) ?? firm.plans[0];
  const account = plan.accounts.find(a => a.size === size) ?? plan.accounts[0];
  const drawdown = funded ? account.fundedMaxDrawdown ?? account.maxDrawdown : account.maxDrawdown;

  const at = Math.max(0, strategies.indexOf(strategy));
  const cycle = (d: number) => strategies.length && setStrategy(strategies[(at + d + strategies.length) % strategies.length]);

  const rec = useMemo(() => (data ? recommend(data.inputs) : null), [data]);
  const warn = data ? warnings(data.inputs, data.choice) : [];
  const chosen = data?.choice?.tier ?? null;
  const upgrade = chosen && rec && RANK[rec.tier] > RANK[chosen];

  const lock = async (tier: Tier, risk: number) => {
    setSaving(tier); setError(null);
    try {
      const choice = await engineApi.choose(username, { strategy, tier, firm_id: firm.id, plan_id: plan.id, account_size: account.size, drawdown, risk_per_trade: risk });
      setData(d => d && { ...d, choice });
      onChosen?.();
    } catch (e) { setError(e instanceof Error ? e.message : 'Could not save'); }
    finally { setSaving(null); }
  };

  /** Risk per trade for a tier, rounded down to $5 */
  const riskFor = (t: Tier) => (data ? Math.floor(drawdown / lossesFor(t, data.inputs.worstStreak) / 5) * 5 : 0);
  return {
    copy, firms, catalog, admin, strategies, strategy, setStrategy, cycle, data, firm, entry, staleDays, plan, setPlanId, setFirmId,
    account, setSize, funded, setFunded, drawdown, showRules, setShowRules, rec, warn, chosen, upgrade, lock, saving, error, riskFor,
  };
}

export function PrescriptionEngine({ username, onChosen }: { username: string; onChosen?: () => void }) {
  const {
    copy, firms, admin, strategy, cycle, data, firm, entry, staleDays, plan, setPlanId, setFirmId,
    account, setSize, funded, setFunded, drawdown, showRules, setShowRules, rec, warn, chosen, upgrade, lock, saving, error,
  } = usePrescription(username, onChosen);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {/* Setup: strategy, firm, plan, size, phase */}
      <div style={{ background: PANEL, boxShadow: pixelFrame(), borderRadius: 'var(--pal-radius, 0)', padding: 16, display: 'flex', flexWrap: 'wrap', gap: 18, alignItems: 'center' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <button type="button" aria-label="Previous strategy" onClick={() => cycle(-1)} style={{ ...chip(false), padding: 4, display: 'grid', placeItems: 'center' }}><ChevronLeft size={14} /></button>
          <span style={{ fontFamily: TITLE_FONT, fontWeight: 900, fontSize: 20, color: ACCENT, minWidth: 56, textAlign: 'center' }}>{strategy}</span>
          <button type="button" aria-label="Next strategy" onClick={() => cycle(1)} style={{ ...chip(false), padding: 4, display: 'grid', placeItems: 'center' }}><ChevronRight size={14} /></button>
        </span>
        <select aria-label="Prop firm" value={firm.id} onChange={e => { const f = firms.find(x => x.id === e.target.value)!; setFirmId(f.id); setPlanId(f.plans[0].id); }}
          style={{ fontFamily: PX_FONT, fontSize: 13, color: ICE, background: FIELD, border: 'none', boxShadow: 'var(--pal-field-shadow)', padding: '8px 10px', borderRadius: 'var(--pal-radius, 0)' }}>
          {firms.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select>
        <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {firm.plans.map(p => <button key={p.id} type="button" onClick={() => setPlanId(p.id)} style={chip(p.id === plan.id)}>{p.name}</button>)}
        </span>
        <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {plan.accounts.map(a => <button key={a.size} type="button" onClick={() => setSize(a.size)} style={chip(a.size === account.size)}>${a.size / 1000}K</button>)}
        </span>
        <span style={{ display: 'flex', gap: 6 }}>
          {[['Eval', false], ['Funded', true]].map(([n, v]) => <button key={String(n)} type="button" onClick={() => setFunded(v as boolean)} style={chip(funded === v)}>{n}</button>)}
        </span>
        <button type="button" onClick={() => setShowRules(s => !s)} aria-expanded={showRules}
          style={{ ...chip(showRules), display: 'flex', alignItems: 'center', gap: 6 }}>
          <ScrollText size={13} /> {showRules ? 'Hide rules' : 'View rules'}
        </button>
        <span style={{ ...label, marginLeft: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
          <span>Max drawdown <b style={{ color: ICE, fontSize: 13 }}>{usd(drawdown)}</b> · {plan.drawdownType}</span>
          {entry?.verified_on && (
            <span style={{ color: staleDays !== null && staleDays > 60 ? LOSS : TEAL }}>
              Rules verified {new Date(entry.verified_on).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
              {staleDays !== null && staleDays > 60 ? ' · may be out of date' : ''}
              {admin && <> · <a href={`/admin/firms?firm=${firm.id}`} style={{ color: ACCENT }}>Edit rules</a></>}
            </span>
          )}
        </span>
        {showRules && (
          <div style={{ flex: '1 1 100%' }}>
            <RulesSnapshot firm={firm} plan={plan} account={account} sources={entry?.source_urls ?? []} verifiedOn={entry?.verified_on ?? null} />
          </div>
        )}
      </div>

      {!data ? <div style={label}>{error ?? 'Reading your trades…'}</div> : !data.ready ? (
        <div style={{ background: PANEL, boxShadow: pixelFrame(), padding: 18, display: 'flex', gap: 10, alignItems: 'center', borderRadius: 'var(--pal-radius, 0)' }}>
          <Lock size={16} color={TEAL} />
          <span style={{ fontFamily: PX_FONT, fontSize: 13, color: ICE }}>Finish Readiness Progression for {strategy} (30 replay days and 30 paper trades, both positive) to generate its prescription.</span>
        </div>
      ) : (
        <>
          {/* Alerts: earned a step up, or a step down is wise */}
          {warn.length > 0 && chosen !== 'steady' && (
            <div role="alert" style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '12px 14px', borderRadius: 'var(--pal-radius, 0)', background: 'var(--pal-area-loss)', boxShadow: `inset 3px 0 0 ${LOSS}` }}>
              <AlertTriangle size={18} color={LOSS} style={{ flex: '0 0 18px', marginTop: 2 }} />
              <span style={{ fontFamily: PX_FONT, fontSize: 13, lineHeight: 1.55, color: ICE }}>
                <b>Consider dropping to {copy.tiers[TIERS[RANK[chosen!] - 1]]}.</b> {warn.join(' · ')}.
              </span>
            </div>
          )}
          {upgrade && warn.length === 0 && (
            <div role="status" style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '12px 14px', borderRadius: 'var(--pal-radius, 0)', background: 'var(--pal-tint-accent)', boxShadow: `inset 3px 0 0 ${WIN}` }}>
              <ArrowUpCircle size={18} color={WIN} style={{ flex: '0 0 18px', marginTop: 2 }} />
              <span style={{ fontFamily: PX_FONT, fontSize: 13, lineHeight: 1.55, color: ICE }}>
                <b>You've earned {copy.tiers[rec!.tier]}.</b> {rec!.why.join(' · ')}.
              </span>
            </div>
          )}

          {/* No personal history yet: say plainly that sizing comes from the strategy's playbook */}
          {!data.inputs.ownData && (
            <div role="note" style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '12px 14px', borderRadius: 'var(--pal-radius, 0)', background: 'rgba(240,160,75,0.12)', boxShadow: 'inset 3px 0 0 #F0A04B' }}>
              <AlertTriangle size={18} color="#F0A04B" style={{ flex: '0 0 18px', marginTop: 2 }} />
              <span style={{ fontFamily: PX_FONT, fontSize: 13, lineHeight: 1.55, color: ICE }}>
                <b style={{ color: '#F0A04B' }}>Sized from the {strategy} playbook, not your trades yet.</b>{' '}
                You have {data.inputs.sample} paper/live trade{data.inputs.sample === 1 ? '' : 's'}, so these numbers assume {strategy}'s expected worst losing streak of {data.inputs.plannedStreak}.
                Once you've logged 20 trades, the engine switches to your own worst streak and the sizes may go up or down. Steady is the safe starting point until then.
              </span>
            </div>
          )}

          {/* The three tiers */}
          <div className="pal-board" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 14 }}>
            {TIERS.map(t => {
              const s = data.inputs.worstStreak;
              const losses = lossesFor(t, s);
              const risk = Math.floor(drawdown / losses / 5) * 5;
              const contracts = data.inputs.riskPerContract ? Math.floor(risk / data.inputs.riskPerContract) : null;
              const isRec = rec?.tier === t, isChosen = chosen === t;
              const tone = TONE[t];
              return (
                <div key={t} style={{ position: 'relative', padding: 18, borderRadius: 'var(--pal-radius, 0)', background: isChosen ? `${tone}18` : FIELD,
                  boxShadow: isChosen ? `inset 0 0 0 1.5px ${tone}, 0 0 22px ${tone}33` : `inset 3px 0 0 ${tone}`, display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontFamily: TITLE_FONT, fontWeight: 900, fontSize: 22, color: tone }}>{copy.tiers[t]}</span>
                    {isRec && <span style={{ ...label, fontSize: 9, fontWeight: 800, color: ON_ACCENT, background: tone, padding: '3px 8px', borderRadius: 10 }}>RECOMMENDED</span>}
                  </div>
                  <div>
                    <div style={{ fontFamily: NUM_FONT, fontWeight: 900, fontSize: 34, color: ICE, lineHeight: 1 }}>{usd(risk)}</div>
                    <div style={{ ...label, marginTop: 4 }}>per trade{contracts !== null ? ` · ${contracts} contract${contracts === 1 ? '' : 's'} at your usual stop` : ''}</div>
                  </div>
                  {/* One pip per loss this size survives before the drawdown is gone */}
                  <div>
                    <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }} aria-label={`${losses} losses until breach`}>
                      {Array.from({ length: Math.min(losses, 24) }, (_, k) => (
                        <span key={k} style={{ width: 10, height: 14, borderRadius: 2, background: k < s ? `${tone}` : `${tone}55` }} />
                      ))}
                    </div>
                    <div style={{ ...label, marginTop: 6 }}><b style={{ color: ICE }}>{losses}</b> losses until breach · your worst streak is {s}</div>
                  </div>
                  <button type="button" onClick={() => lock(t, risk)} disabled={saving !== null}
                    style={{ marginTop: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '10px 14px', border: 'none', borderRadius: 'var(--pal-radius, 0)', cursor: 'pointer',
                      fontFamily: PX_FONT, fontSize: 12, fontWeight: 800, background: isChosen ? 'transparent' : tone, color: isChosen ? tone : ON_ACCENT, boxShadow: isChosen ? `inset 0 0 0 1px ${tone}` : 'none' }}>
                    {isChosen ? <><Check size={14} /> Locked in</> : saving === t ? 'Saving…' : `Trade ${copy.tiers[t]}`}
                  </button>
                </div>
              );
            })}
          </div>

          {/* Why: the numbers behind it */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 22, padding: '12px 16px', borderRadius: 'var(--pal-radius, 0)', background: FIELD }}>
            <span style={label}>Worst losing streak <b style={{ color: ICE }}>{data.inputs.worstStreak}</b> {data.inputs.ownData ? `(last ${data.inputs.sample} paper + live trades)` : `(planned: you have ${data.inputs.sample} of 20 trades)`}</span>
            <span style={label}>Paper <b style={{ color: ICE }}>{data.inputs.paper.trades}</b> · {r(data.inputs.paper.expectancyR)}</span>
            <span style={label}>Live <b style={{ color: ICE }}>{data.inputs.live.trades}</b>{data.inputs.live.trades ? ` · ${r(data.inputs.live.expectancyR)}` : ''}</span>
            <span style={label}>Payouts <b style={{ color: ICE }}>{data.inputs.payouts}</b></span>
            {rec && <span style={label}>Recommendation: <b style={{ color: ICE }}>{copy.tiers[rec.tier]}</b>, {rec.why.join(' · ')}</span>}
          </div>
          {!chosen && <div style={{ ...label, color: ACCENT }}>Lock in a tier to open Live Trading for {strategy}.</div>}
          {error && <div style={{ ...label, color: LOSS }}>{error}</div>}
        </>
      )}
    </div>
  );
}
